<template>
  <div class="action-bar">
    <div data-intercom-target="intercom-action-bar">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex gap-xs gap-md-md">
          <FeedItemActionButton
            v-if="!reportedByUser"
            text="Like"
            :highlight="likedByUser"
            :disabled="!clientCanInteractCommunity"
            aria-label="Toggle like button"
            @click="$emit('like-click')">
            <template #icon>
              <SvgLikeFilledIn
                v-if="likedByUser"
                class="color-plum"/>
              <SvgLike v-else/>
            </template>
          </FeedItemActionButton>
          <FeedItemActionButton
            v-if="!reportedByUser"
            :text="`Comment ${comments && comments.count > 0 ? `(${comments.count})` : ''}`"
            :disabled="!clientCanInteractCommunity"
            aria-label="Comment on look"
            @click="$emit('comment-click')">
            <template #icon>
              <SvgComment/>
            </template>
          </FeedItemActionButton>
          <FeedItemActionButton
            :text="reportedByUser ? 'Reported' : 'Report'"
            :highlight="reportedByUser"
            :disabled="!clientCanInteractCommunity || reportedByUser"
            @click="reportClick">
            <template #icon>
              <SvgFlag :filled="reportedByUser"/>
            </template>
          </FeedItemActionButton>
        </div>
        <feed-item-timestamp
          v-if="timestamp"
          :timestamp="timestamp"/>
      </div>
      <div
        v-if="likesAndFavorites.first"
        class="mt-3 small-text d-flex align-items-center">
        <feed-item-action-icons
          :show-likes="likesAndFavorites && likesAndFavorites.totalLikes > 0"
          :show-favorites="likesAndFavorites && likesAndFavorites.totalFavorites > 0"/>
        <text-link
          type="router-link"
          variant="secondary"
          :to="{ name: 'profile', params: { id: likesAndFavorites.first.id } }"
          @click="onNavigateToProfile(likesAndFavorites.first.id)">
          {{ getName(likesAndFavorites.first) }}
        </text-link>
        <span v-if="likesAndFavorites.total > 1">
          &nbsp;and
          <text-link
            variant="secondary"
            @click="onViewLikesClick">
            {{ likesAndFavorites.total - 1 }} other<span v-if="likesAndFavorites.total - 2 > 0">s</span>
          </text-link>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import FeedItemTimestamp from './FeedItemTimestamp'
import TextLink from '../../global/sequin/TextLink'
import { mapState, mapGetters } from 'vuex'
import { mapActions } from 'pinia'
import FeedItemActionIcons from './FeedItemActionIcons'
import { useOverlaysStore } from '@/stores/overlays.js'
import SvgLikeFilledIn from '@/components/global/svg/SvgLikeFilledIn.vue'
import SvgLike from '@/components/global/svg/SvgLike.vue'
import SvgComment from '@/components/global/svg/SvgComment.vue'
import FeedItemActionButton from './FeedItemActionButton.vue'
import SvgFlag from '@shared/components/icons/SvgFlag.vue'

export default {
  components: {
    FeedItemActionIcons,
    FeedItemTimestamp,
    FeedItemActionButton,
    TextLink,
    SvgLikeFilledIn,
    SvgLike,
    SvgComment,
    SvgFlag
  },
  props: {
    feedItemId: {
      type: String,
      required: true
    },
    likesAndFavorites: {
      type: Object,
      required: true
    },
    comments: {
      type: Object,
      required: true
    },
    timestamp: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState('client', [
      'id'
    ]),
    ...mapState('community', [
      'userFeedLikes',
    ]),
    ...mapGetters('community', [
      'clientCanInteractCommunity',
      'feedItemReports'
    ]),
    likedByUser () {
      return this.userFeedLikes && this.userFeedLikes.includes(this.feedItemId)
    },
    reportedByUser () {
      return this.feedItemReports.has(this.feedItemId)
    }
  },
  methods: {
    ...mapActions(useOverlaysStore, [
      'openModal'
    ]),
    getName (like) {
      if (like.id === this.id) {
        return 'You'
      } else if (like.lastName) {
        return `${like.firstName} ${like.lastName}`
      }
      return `${like.firstName}`
    },
    onViewLikesClick () {
      if (this.isMobile) {
        this.$router.push({ name: 'feed-item-likes', params: { id: this.feedItemId } })
      } else {
        this.openModal({ name: 'view-likes', context: { feedItemId: this.feedItemId } })
      }
    },
    onNavigateToProfile (client) {
      this.$store.commit('community/SET_CLIENT_INFO', client)
    },
    reportClick () {
      this.openModal({ name: 'report-content', context: { feedItemId: this.feedItemId } })
    }
  }
}
</script>

<style lang="scss" scoped>
.action-count {
  margin: 6px 0 0 6px;
  color: $pewter;
  font-size: $small-text-font-size;
  line-height: 18px;
}

.action-bar {
  padding: 0 $spacing-md;
}
@include media-tablet-or-larger() {
  .action-bar {
    padding: 0 28px;
  }
}

.action-button {
  padding: 1px;

  svg {
    color: $pewter;

    &.filled-in {
      color: $plum;
    }
  }

  &:hover {
    svg {
      color: $plum;
    }
  }
}

</style>
